import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SmartContext } from '../../../../library/Core/SmartContext';
import { axiosClient } from '../../../../library/Service/axiosClient';
import { getDomainValueForCode, getFlexColDomainValueForCode, isEmpty, isSuperAdmin } from '../../../../library/Core/SmartFunctions';
import { isAlumniProfile } from '../service/student-profile-view.service';
import AddOutreachModal from './AddOutreachModal';
import SelectInstituteOptionLayout from './admin/SelectInstituteOptionLayout';
import OutreachLogPageConfig from './OutreachLogPageConfig.json';
import moment from 'moment';
import Swal from 'sweetalert2';
import { Tooltip } from 'bootstrap';
import SessionContext from '../../../../library/Core/SessionContext';

const OutreachLog = () => {
    const { state, dispatch } = useContext(SmartContext);
    const navigate = useNavigate();
    const { sessionState } = useContext(SessionContext);
    const model = state.internal.model;

    const [isConfigAvailable, setIsConfigAvailable] = useState(false);
    const [allLogs, setAllLogs] = useState<any[]>([]);
    const [filterTeam, setFilterTeam] = useState('');
    const [selectedLog, setSelectedLog] = useState<any>(null);
    const [showClickToCallModal, setShowClickToCallModal] = useState(false);
    const [clickToCallForm, setClickToCallForm] = useState({
        team: '',
        purposeCategory: '',
        purposeSubCategory: '',
        callMode: ''
    });

    const instituteId =
        state?.data?.selectedInstitute?.instituteId ||
        state?.data?.userInstitutes?.[0]?.instituteId;

    const SUCCESSFUL_DISPOSITIONS = ['INFORMATION_COLLECTED', 'INFORMATION_COLLECTED_PARTLY',
        'INFORMATION_COLLECTED_WHATSAPP', 'CONSENT_GIVEN', 'PLEDGE_RECEIVED', 'EVENT_CONFIRMED', 'PROFILE_COMPLETED'];

    const displayLogs = useMemo(
        () => filterTeam ? allLogs.filter((l) => l.team === filterTeam) : allLogs,
        [allLogs, filterTeam]
    );

    const formatDuration = (duration: string) => {
        if (!duration) return '';
        const parts = String(duration).split(':');
        if (parts.length === 2) return `${parts[0]}m:${parts[1]}s`;
        if (parts.length === 3) return `${parts[0]}h:${parts[1]}m:${parts[2]}s`;
        return duration;
    };

    const summary = useMemo(() => {
        const totalSec = displayLogs.reduce((acc, l) => {
            if (!l.duration) return acc;
            const parts = String(l.duration).split(':');
            if (parts.length === 2) return acc + parseInt(parts[0]) * 60 + parseInt(parts[1]);
            if (parts.length === 3) return acc + parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
            return acc;
        }, 0);
        const totalMins = Math.floor(totalSec / 60);
        const totalSecsRem = totalSec % 60;
        const total_duration = `${totalMins}m:${String(totalSecsRem).padStart(2, '0')}s`;
        return {
            total: displayLogs.length,
            teams_engaged: new Set(displayLogs.map((l) => l.team).filter(Boolean)).size,
            successful: displayLogs.filter((l) => SUCCESSFUL_DISPOSITIONS.includes((l.disposition || '').toUpperCase())).length,
            with_proofs: displayLogs.filter((l) => l.attachmentFile).length,
            total_duration
        };
    }, [displayLogs]);

    useEffect(() => {
        Promise.resolve(OutreachLogPageConfig)
            .then((config) => {
                dispatch({
                    type: 'DATA_INIT',
                    payload: {
                        formConfig: config,
                        data: { ...state.data, outreachLog: {} },
                        domain: new Map([...state.domain]),
                        internal: state.internal,
                        routeInfo: state.routeInfo
                    }
                });
                setIsConfigAvailable(true);
            })
            .catch((error) => console.error('Error loading outreach config:', error));

        fetchAllLogs();
    }, []);

    const fetchAllLogs = async () => {
        try {
            const res = await axiosClient().post(
                `${process.env.REACT_APP_COMMON_API}/outreach-log/get-all`,
                { studentId: model.id, instituteId }
            );
            if (res.data.success) {
                setAllLogs(res.data.data);
                return res.data.data;
            }
            return [];
        } catch (error) {
            console.error('Error fetching outreach logs:', error);
            return [];
        }
    };

    const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setFilterTeam(e.target.value);
    };

    const getDispositionBadgeClass = (dispositionCode: string) => {
        const parentCode = state.domain.get('DISPOSITION_SUBCATEGORY')?.find((el: any) => el.code === dispositionCode)?.parentCode;
        switch (parentCode) {
            case 'SUCCESSFUL':         return 'alert alert-success';
            case 'FOLLOW_UP_NEEDED':   return 'alert alert-warning';
            case 'CLOSED_UNREACHABLE': return 'alert alert-danger';
            default:                   return 'alert alert-secondary';
        }
    };

    const getTeamBadgeClass = (teamCode: string) => {
        switch (teamCode) {
            case 'ALUMNI_DATA':       return 'bg-success bg-opacity-10 border border-success border-opacity-25 rounded-2 text-success';
            case 'FUNDRAISING':       return 'bg-lite-purple-faf5ff border border-success border-opacity-25 rounded-2 text-purple';
            case 'MARKETING_ENGAGEMENT': return 'bg-warning bg-opacity-10 border border-warning border-opacity-25 rounded-2 text-warning';
            case 'DK_COLLECTION':     return 'bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded-2 text-primary';
            case 'EVENTS':            return 'bg-danger bg-opacity-10 border border-danger border-opacity-25 rounded-2 text-danger';
            default:                  return 'bg-secondary bg-opacity-10 border border-secondary border-opacity-25 rounded-2 text-secondary';
        }
    };

    const handleAddOutreach = () => {
        setSelectedLog(null);
        dispatch({ type: 'RESET_VALIDATION_ERRORS' });
        dispatch({ type: 'REFRESH_DATA', payload: { data: { ...state.data, outreachLog: {} } } });
        dispatch({ type: 'TOGGLE_MODAL_VISIBILITY', payload: { modalName: 'showAddOutreachModal' } });
    };

    const handleClickToCallFormSubmit = () => {
        // Validate all fields are filled
        if (!clickToCallForm.team || !clickToCallForm.purposeCategory || !clickToCallForm.purposeSubCategory || !clickToCallForm.callMode) {
            Swal.fire('Validation Error', 'Please fill all required fields: Team, Purpose Category, Sub Category, and Call Mode.', 'error');
            return;
        }

        // Close modal
        setShowClickToCallModal(false);

        // Show confirmation dialog
        Swal.fire({
            title: 'Make a Call?',
            text: `This will initiate a call to ${state?.data?.firstName} ${state?.data?.lastName}. Do you want to proceed?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, call now',
            cancelButtonText: 'No, cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Initiating Call...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });
                const apiUrl = `${process.env.REACT_APP_COMMON_API}/acefone/click-to-call/`;
                // Determine agentNumber based on call mode
                let agentNumber = sessionState?.mobileNumber;
                if (clickToCallForm.callMode === 'extension' && adminAdditionalInfo?.acefone_agent_id) {
                    agentNumber = adminAdditionalInfo.acefone_agent_id;
                } else if (clickToCallForm.callMode === 'phone' && adminAdditionalInfo?.agent_mobile_number) {
                    agentNumber = adminAdditionalInfo.agent_mobile_number;
                }
                axiosClient().post(apiUrl, {
                    agentNumber: agentNumber,
                    phoneNumber: `${state.data.profile.contactMobileCountryCode}${state.data.profile.contactMobileNumber}`,
                    studentId: state.data?.id,
                    instituteId: state.data.selectedInstitute?.instituteId || state.data.userInstitutes?.[0]?.instituteId,
                    team: clickToCallForm.team,
                    purposeCategory: clickToCallForm.purposeCategory,
                    purposeSubCategory: clickToCallForm.purposeSubCategory
                }).then(async (res) => {
                    // print the response in console for debugging
                    console.log('Call initiated successfully');
                    console.log('API Response:', res.data);
                    Swal.close();
                    // Show toast notification
                    Swal.fire({
                        position: 'top-end',
                        icon: 'success',
                        title: 'Call initiated successfully!',
                        showConfirmButton: false,
                        timer: 3000,
                        toast: true
                    });
                    // Reset form after successful call
                    setClickToCallForm({
                        team: '',
                        purposeCategory: '',
                        purposeSubCategory: '',
                        callMode: ''
                    });

                    const fetchedLogs = await fetchAllLogs();
                    // if (fetchedLogs && fetchedLogs.length > 0) {
                    //     handleEditOutreach(fetchedLogs[0]);
                    // }
                    
                }).catch((error) => {
                    console.error('Error initiating call:', error);
                    let errorMessage = 'An error occurred while initiating the call. Please try again.';
                    if (error?.data?.error) {
                        errorMessage = error.data.error;
                    } else if (error.message) {
                        errorMessage = error.message;
                    }
                    Swal.fire('', errorMessage, 'error');
                });
            }
        });
    };

    const handleClickToCall = () => {
        // Open modal instead of initiating call directly
        setShowClickToCallModal(true);
        // Prepopulate form from adminAdditionalInfo
        const teamValue = adminAdditionalInfo?.team || '';
        const callModeValue = (adminAdditionalInfo?.call_mode === 'extension') ? 'extension' : (adminAdditionalInfo?.call_mode ? adminAdditionalInfo.call_mode : '');
        setClickToCallForm({
            team: teamValue,
            purposeCategory: '',
            purposeSubCategory: '',
            callMode: callModeValue
        });
    };

    const handleClickToCallModalClose = () => {
        setShowClickToCallModal(false);
        setClickToCallForm({
            team: '',
            purposeCategory: '',
            purposeSubCategory: '',
            callMode: ''
        });
    };

    const handleEditOutreach = async (log: any) => {
        try {
            // Show loading dialog
            Swal.fire({
                title: 'Loading...',
                html: 'Fetching outreach log details...',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            // Fetch the latest log data before opening the modal
            const res = await axiosClient().post(
                `${process.env.REACT_APP_COMMON_API}/outreach-log/get-by-id`,
                { id: log.id }
            );
            
            const latestLog = res.data.data;
            setSelectedLog(latestLog);
            dispatch({ type: 'RESET_VALIDATION_ERRORS' });
            dispatch({
                type: 'REFRESH_DATA',
                payload: {
                    data: {
                        ...state.data,
                        outreachLog: {
                            id: latestLog.id,
                            team: latestLog.team,
                            purposeCategory: latestLog.purpose_category,
                            purposeSubCategory: latestLog.purpose_subcat,
                            disposition: latestLog.disposition,
                            talkedTo: latestLog.talkedTo,
                            mode: latestLog.mode,
                            duration: latestLog.duration,
                            comment: latestLog.comment,
                            attachmentFile: latestLog.attachmentFile
                        }
                    }
                }
            });
            Swal.close();
            dispatch({ type: 'TOGGLE_MODAL_VISIBILITY', payload: { modalName: 'showAddOutreachModal' } });
        } catch (error) {
            console.error('Error fetching outreach log details:', error);
            Swal.fire('Error', 'Failed to load outreach log details. Please try again.', 'error');
        }
    };

    const teamOptions = state?.domain?.get('OUTREACH_TEAM_CODE') ?? [];

    // Parse adminAdditionalInfo from sessionState
    const adminAdditionalInfo = useMemo(() => {
        try {
            if (!sessionState?.adminAdditionalInfo) {
                return null;
            }
            return JSON.parse(sessionState.adminAdditionalInfo);
        } catch (error) {
            console.error('Error parsing adminAdditionalInfo:', error);
            return null;
        }
    }, [sessionState]);

    // Check if click-to-call is allowed based on sessionState
    const isClickToCallAllowed = useMemo(() => {
        return adminAdditionalInfo?.allow_click_to_call === true;
    }, [adminAdditionalInfo]);

    // Show Capture Commitment only when the selected institute has at least one
    // commitment path opted in (INSTITUTE_DOMAIN.flex_col.commitment_paths not null / non-empty).
    const isCommitmentEnabled = useMemo(() => {
        if (isEmpty(instituteId)) return false;
        const flexCol = getFlexColDomainValueForCode(String(instituteId), 'INSTITUTE_DOMAIN', state);
        if (isEmpty(flexCol)) return false;
        try {
            const paths = JSON.parse(flexCol as string)?.commitment_paths;
            return Array.isArray(paths) && paths.length > 0;
        } catch {
            return false;
        }
    }, [instituteId, state?.domain]);

    // Per-log entry into the capture form. The clicked log is an admin_call_log
    // row, so its id is passed as the adminCallLogId path segment — the capture
    // form then hides the Call Session dropdown / call button and saves against
    // this call.
    const handleCaptureCommitment = (log: any) => {
        if (isEmpty(instituteId) || isEmpty(model?.id)) return;
        window.open(`/commitment/capture/${instituteId}/${model.id}/${log.id}`, '_blank', 'noopener,noreferrer');
    };

    // Initialise Bootstrap tooltips (same mechanism as the Auto-login Link "View"
    // button in AdminAlumniProfileHeader). Re-runs when the rendered rows change.
    useEffect(() => {
        const triggers = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        const instances = triggers.map((el: any) => new Tooltip(el));
        return () => instances.forEach((t: any) => t.dispose());
    }, [displayLogs, isCommitmentEnabled]);

    // Filter subcategories based on selected purpose category
    const filteredSubCategories = useMemo(() => {
        if (!clickToCallForm.purposeCategory) {
            return [];
        }
        const allSubCategories = state?.domain?.get('OUTREACH_PURPOSE_SUBCATEGORY_CODE') ?? [];
        return allSubCategories.filter((subCat: any) => subCat.parentCode === clickToCallForm.purposeCategory);
    }, [clickToCallForm.purposeCategory, state?.domain]);

    return (
        <>
            <div className="underGraduate-detail">
                <div className="blockHeader">
                    <div className="row g-3 align-items-center">

                        {/* Title + Filter + Add button */}
                        <div className="col-12">
                            <div className="row g-2 align-items-center">
                                <div className="col-sm">
                                    <div className="title">
                                        <i className="icon icon-world"></i> <span>Outreach Log</span>
                                    </div>
                                </div>
                                {state?.data?.userInstitutes?.length > 1 && (
                                    <SelectInstituteOptionLayout />
                                )}
                                {isSuperAdmin() && isAlumniProfile(state) && (
                                    <div className="col-auto">
                                        <a
                                            href="javascript:;"
                                            className="btn btn-green text-decoration-none rounded-2 px-2 height-45"
                                            onClick={() => {
                                                if (!instituteId || !model?.id) {
                                                    Swal.fire({
                                                        icon: 'info',
                                                        text: 'An alumnus and institute are required before adding a story.',
                                                    });
                                                    return;
                                                }
                                                navigate(`/support/story/new?narratorId=${model.id}&instituteId=${instituteId}`);
                                            }}>
                                            <i className="icon-plus font-18 position-relative me-1"></i>
                                            <span className="line-height-16">Add Story</span>
                                        </a>
                                    </div>
                                )}
                                <div className="col-auto">
                                    <select
                                        className="form-select form-select-sm"
                                        value={filterTeam}
                                        onChange={handleFilterChange}
                                        style={{ minWidth: '160px' }}>
                                        <option value="">All Teams</option>
                                        {teamOptions.map((opt: any) => (
                                            <option key={opt.code} value={opt.code}>{opt.value}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-auto">
                                    <a
                                        href="javascript:;"
                                        className="btn btn-green text-decoration-none rounded-2 px-2 height-45"
                                        onClick={handleAddOutreach}>
                                        <i className="icon-plus font-18 position-relative me-1"></i>
                                        <span className="line-height-16">Add Outreach</span>
                                    </a>
                                </div>
                                {isClickToCallAllowed && (
                                    <div className="col-auto">
                                        <a 
                                            href="javascript:;"
                                            id="click-to-call-btn"
                                            className="btn btn-green text-decoration-none rounded-2 px-2 height-45"
                                            onClick={handleClickToCall}> 
                                            <i className="bi bi-telephone-outbound"></i>
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Dashboard count cards */}
                        <div className="col-12">
                            <div className="row g-3 mx-0">
                                <div className="col">
                                    <div className="bg-white p-4 custom-shadow h-100 rounded-4">
                                        <div className="row g-2">
                                            <div className="col-auto"><i className="bi bi-chat-left-text font-24 text-gray-64"></i></div>
                                            <div className="col">
                                                <h3 className="font-24 font-600 mb-0">{summary.total}</h3>
                                                <p className="font-14 font-500 text-gray-64 mb-0">Total Outreach</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="bg-white p-4 custom-shadow h-100 rounded-4">
                                        <div className="row g-2">
                                            <div className="col-auto"><i className="bi bi-trophy font-24 text-gray-64"></i></div>
                                            <div className="col">
                                                <h3 className="font-24 font-600 mb-0">{summary.teams_engaged}</h3>
                                                <p className="font-14 font-500 text-gray-64 mb-0">Teams Engaged</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="bg-success bg-opacity-10 border border-success border-opacity-25 p-4 h-100 rounded-4 text-success">
                                        <div className="row g-2">
                                            <div className="col-auto"><i className="bi bi-check2-circle font-24"></i></div>
                                            <div className="col">
                                                <h3 className="font-24 font-600 mb-0">{summary.successful}</h3>
                                                <p className="font-14 font-500 mb-0">Successful</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="bg-lite-purple-faf5ff border-purple-9812fa p-4 h-100 rounded-4 text-purple">
                                        <div className="row g-2">
                                            <div className="col-auto"><i className="bi bi-file-earmark-text font-24"></i></div>
                                            <div className="col">
                                                <h3 className="font-24 font-600 mb-0">{summary.with_proofs}</h3>
                                                <p className="font-14 font-500 mb-0">With Proofs</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="bg-warning bg-opacity-10 border border-warning border-opacity-25 p-4 h-100 rounded-4 text-gray-41">
                                        <div className="row g-2">
                                            <div className="col-auto"><i className="bi bi-clock font-24"></i></div>
                                            <div className="col">
                                                <h3 className="font-24 font-600 mb-0">{summary.total_duration}</h3>
                                                <p className="font-14 font-500 mb-0">Outreach Duration</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* All outreach logs */}
                <div className="outreach-wrap">
                    {displayLogs.length > 0 ? (
                        <ul>
                            {displayLogs.map((log: any, index: number) => (
                                <li key={log.id || index}>
                                    <div className="dot green-dot"></div>
                                    <div className="bg-white custom-shadow h-100 rounded-4 p-3 p-sm-4 position-relative">
                                        {isCommitmentEnabled && (
                                            <button
                                                className="bi bi-clipboard-check font-24 position-absolute top-20 right-50 cursor-pointer border-0 bg-transparent link-success btn-white-tooltip"
                                                data-bs-toggle="tooltip"
                                                data-bs-html="true"
                                                title=""
                                                data-bs-original-title="Capture Commitment"
                                                onClick={() => handleCaptureCommitment(log)}
                                            ></button>
                                        )}
                                        <button
                                            className="bi bi-pencil-square font-24 position-absolute top-20 right-20 cursor-pointer border-0 bg-transparent link-secondary"
                                            onClick={() => handleEditOutreach(log)}
                                        ></button>
                                        <div className="row g-3">
                                            {/* Row 1: Team · Mode · Date */}
                                            <div className="col-12">
                                                <div className="row g-2 align-items-center">
                                                    {log.team && (
                                                        <div className="col-auto">
                                                            <div className={`px-2 py-1 font-500 ${getTeamBadgeClass(log.team)}`}>
                                                                {getDomainValueForCode(log.team, 'OUTREACH_TEAM_CODE', state) || log.team}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {log.mode && (
                                                        <div className="col-auto">
                                                            <div className="px-2 py-1 font-500 bg-lite-purple-faf5ff border-purple-9812fa rounded-2 text-purple">
                                                                {getDomainValueForCode(log.mode, 'OUTREACH_MODE', state) || log.mode}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {log.created_at && (
                                                        <div className="col-auto font-500 text-gray-84">
                                                            {moment(log.created_at).format('MMM DD, YYYY')}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Row 2: Caller · Talked to · Duration */}
                                            <div className="col-12">
                                                <span className="font-500">{log.created_by || '—'}</span>
                                                {log.talkedTo && (
                                                    <>
                                                        <i className="font-24 font-600"> · </i>
                                                        Talked to: {getDomainValueForCode(log.talkedTo, 'OUTREACH_TALKED_TO_CODE', state) || log.talkedTo}
                                                    </>
                                                )}
                                                {log.duration && (
                                                    <span className="text-primary font-500">
                                                        <i className="font-24 font-600"> · </i>{formatDuration(log.duration)}
                                                    </span>
                                                )}
                                                {log.updated_by_name && (
                                                    <span className="text-muted font-12 ms-2">
                                                        <i className="font-24 font-600"> · </i>
                                                        Last Edited by: <span className="font-500">{log.updated_by_name}</span>
                                                    </span>
                                                )}
                                            </div>

                                            {/* Row 3: Purpose category · Subcategory · Disposition */}
                                            {(log.purpose_category || log.purpose_subcat || log.disposition) && (
                                                <div className="col-12">
                                                    <div className="row g-2">
                                                        {(log.purpose_category || log.purpose_subcat) && (
                                                            <div className="col-auto">
                                                                <div className="px-2 py-1 font-500 alert alert-secondary mb-0">
                                                                    {[
                                                                        log.purpose_category && (getDomainValueForCode(log.purpose_category, 'OUTREACH_PURPOSE_CATEGORY_CODE', state) || log.purpose_category),
                                                                        log.purpose_subcat && (getDomainValueForCode(log.purpose_subcat, 'OUTREACH_PURPOSE_SUBCATEGORY_CODE', state) || log.purpose_subcat)
                                                                    ].filter(Boolean).join(' > ')}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {log.disposition && (
                                                            <div className="col-auto">
                                                                <div className={`px-2 py-1 font-500 mb-0 ${getDispositionBadgeClass(log.disposition)}`}>
                                                                    {getDomainValueForCode(log.disposition, 'DISPOSITION_SUBCATEGORY', state) || log.disposition}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Row 4: Comment */}
                                            {log.comment && (
                                                <div className="col-12">
                                                    <div className="p-3 bg-secondary bg-opacity-10 border border-light-subtle rounded-3">
                                                        "{log.comment}"
                                                    </div>
                                                </div>
                                            )}

                                            {/* Row 5: Attachments (comma-separated URLs) */}
                                            {log.attachmentFile && (
                                                <div className="col-12">
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {String(log.attachmentFile).split(',').map((url: string, idx: number) => (
                                                            <a
                                                                key={idx}
                                                                href={url.trim()}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="p-3 py-2 bg-secondary bg-opacity-10 border border-light-subtle rounded-3 d-inline-block text-decoration-none text-dark">
                                                                <i className="bi bi-paperclip me-1"></i>
                                                                {decodeURIComponent(url.trim().split('/').pop() || `File ${idx + 1}`)}
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-center py-4">
                            <span>No Outreach Data Found</span>
                        </div>
                    )}
                </div>
            </div>

            {isConfigAvailable && (
                <AddOutreachModal
                    editLog={selectedLog}
                    onSuccess={() => fetchAllLogs()}
                />
            )}

            {/* Click to Call Modal */}
            {showClickToCallModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Initiate Click-to-Call</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={handleClickToCallModalClose}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <form>
                                    {/* Team Dropdown */}
                                    <div className="mb-3">
                                        <label className="form-label font-500">
                                            Team <span className="text-danger">*</span>
                                        </label>
                                        <select 
                                            className="form-select"
                                            value={clickToCallForm.team}
                                            onChange={(e) => setClickToCallForm({ ...clickToCallForm, team: e.target.value })}
                                        >
                                            <option value="">Select Team</option>
                                            {state?.domain?.get('OUTREACH_TEAM_CODE')?.map((opt: any) => (
                                                <option key={opt.code} value={opt.code}>{opt.value}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Purpose Category Dropdown */}
                                    <div className="mb-3">
                                        <label className="form-label font-500">
                                            Purpose Category <span className="text-danger">*</span>
                                        </label>
                                        <select 
                                            className="form-select"
                                            value={clickToCallForm.purposeCategory}
                                            onChange={(e) => setClickToCallForm({ ...clickToCallForm, purposeCategory: e.target.value, purposeSubCategory: '' })}
                                        >
                                            <option value="">Select Purpose Category</option>
                                            {state?.domain?.get('OUTREACH_PURPOSE_CATEGORY_CODE')?.map((opt: any) => (
                                                <option key={opt.code} value={opt.code}>{opt.value}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Purpose Sub Category Dropdown */}
                                    <div className="mb-3">
                                        <label className="form-label font-500">
                                            Sub Category <span className="text-danger">*</span>
                                        </label>
                                        <select 
                                            className="form-select"
                                            value={clickToCallForm.purposeSubCategory}
                                            onChange={(e) => setClickToCallForm({ ...clickToCallForm, purposeSubCategory: e.target.value })}
                                            disabled={!clickToCallForm.purposeCategory}
                                        >
                                            <option value="">Select Sub Category</option>
                                            {filteredSubCategories?.map((opt: any) => (
                                                <option key={opt.code} value={opt.code}>{opt.value}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Call Mode Radio Buttons */}
                                    <div className="mb-3">
                                        <label className="form-label font-500">
                                            Call Mode <span className="text-danger">*</span>
                                        </label>
                                        <div className="d-flex gap-3">
                                            <div className="form-check">
                                                <input 
                                                    className="form-check-input" 
                                                    type="radio" 
                                                    id="extensionMode"
                                                    name="callMode"
                                                    value="extension"
                                                    checked={clickToCallForm.callMode === 'extension'}
                                                    onChange={(e) => setClickToCallForm({ ...clickToCallForm, callMode: e.target.value })}
                                                />
                                                <label className="form-check-label" htmlFor="extensionMode">
                                                    Extension
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input 
                                                    className="form-check-input" 
                                                    type="radio" 
                                                    id="phoneMode"
                                                    name="callMode"
                                                    value="phone"
                                                    checked={clickToCallForm.callMode === 'phone'}
                                                    onChange={(e) => setClickToCallForm({ ...clickToCallForm, callMode: e.target.value })}
                                                />
                                                <label className="form-check-label" htmlFor="phoneMode">
                                                    Phone
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={handleClickToCallModalClose}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary" 
                                    onClick={handleClickToCallFormSubmit}
                                >
                                    Proceed to Call
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OutreachLog;
